import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  ListTodo,
  Plus,
  Search,
  FileText,
  Target,
  Settings,
  Users,
  CheckCircle
} from 'lucide-react';
import { OnboardingTask, OnboardingTaskFormData } from '@/types/models';

interface AddOnboardingTaskDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: OnboardingTaskFormData) => void;
  initialData?: OnboardingTaskFormData;
  isEdit?: boolean;
  // New props for adding tasks to existing onboarding
  mode?: 'create' | 'add-to-onboarding';
  availableTasks?: OnboardingTask[];
  existingTaskIds?: string[];
  onAddTasksToOnboarding?: (taskIds: string[]) => void;
}

export const AddOnboardingTaskDialog: React.FC<AddOnboardingTaskDialogProps> = ({
  open,
  onOpenChange,
  onSubmit,
  initialData,
  isEdit,
  mode = 'create',
  availableTasks = [],
  existingTaskIds = [],
  onAddTasksToOnboarding,
}) => {
  const [form, setForm] = useState<OnboardingTaskFormData>({
    title: '',
    description: '',
    category: '',
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [selectedTaskIds, setSelectedTaskIds] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (initialData) {
      setForm({
        title: initialData.title || '',
        description: initialData.description || '',
        category: initialData.category || '',
      });
    } else {
      setForm({ title: '', description: '', category: '' });
    }
    setErrors({});
    setSelectedTaskIds([]);
    setSearchQuery('');
  }, [open, initialData]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox' && e.target instanceof HTMLInputElement) {
      setForm((prev) => ({
        ...prev,
        [name]: (e.target as HTMLInputElement).checked,
      }));
    } else {
      setForm((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleCategoryChange = (value: string) => {
    setForm((prev) => ({
      ...prev,
      category: value,
    }));
    // Clear category error when user selects a category
    if (errors.category) {
      setErrors((prev) => ({ ...prev, category: '' }));
    }
  };

  const validate = () => {
    const newErrors: { [key: string]: string } = {};
    if (!form.title.trim()) newErrors.title = 'Title is required';
    if (!form.description?.trim()) newErrors.description = 'Description is required';
    if (!form.category?.trim()) newErrors.category = 'Category is required';
    return newErrors;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    onSubmit({
      title: form.title,
      description: form.description,
      category: form.category,
    });
    onOpenChange(false);
  };

  const handleAddTasksToOnboarding = () => {
    if (selectedTaskIds.length === 0) {
      setErrors({ tasks: 'Please select at least one task to add' });
      return;
    }
    onAddTasksToOnboarding?.(selectedTaskIds);
    onOpenChange(false);
  };

  const handleTaskSelection = (taskId: string, checked: boolean) => {
    if (checked) {
      setSelectedTaskIds(prev => [...prev, taskId]);
    } else {
      setSelectedTaskIds(prev => prev.filter(id => id !== taskId));
    }
    setErrors({});
  };

  const handleSelectAllInCategory = (category: string) => {
    const categoryTasks = availableTasks.filter(task =>
      task.category === category &&
      !existingTaskIds.includes(task._id)
    );
    const categoryTaskIds = categoryTasks.map(task => task._id);
    setSelectedTaskIds(prev => [...new Set([...prev, ...categoryTaskIds])]);
  };

  const handleDeselectAllInCategory = (category: string) => {
    const categoryTasks = availableTasks.filter(task => task.category === category);
    const categoryTaskIds = categoryTasks.map(task => task._id);
    setSelectedTaskIds(prev => prev.filter(id => !categoryTaskIds.includes(id)));
  };

  // Filter tasks based on search query
  const filteredTasks = availableTasks.filter(task =>
    task.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    task.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
    task.category.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Group tasks by category
  const tasksByCategory = filteredTasks.reduce((acc, task) => {
    if (!acc[task.category]) {
      acc[task.category] = [];
    }
    acc[task.category].push(task);
    return acc;
  }, {} as Record<string, OnboardingTask[]>);

  const getCategoryIcon = (category: string) => {
    switch (category.toLowerCase()) {
      case 'hr & administration':
        return <Users className="h-4 w-4 text-blue-500" />;
      case 'it & technical':
        return <Settings className="h-4 w-4 text-green-500" />;
      case 'training & orientation':
        return <Target className="h-4 w-4 text-purple-500" />;
      case 'documentation':
        return <FileText className="h-4 w-4 text-orange-500" />;
      default:
        return <ListTodo className="h-4 w-4 text-gray-500" />;
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category.toLowerCase()) {
      case 'hr & administration':
        return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'it & technical':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'training & orientation':
        return 'text-purple-600 bg-purple-50 border-purple-200';
      case 'documentation':
        return 'text-orange-600 bg-orange-50 border-orange-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const renderCreateTaskForm = () => (
    <form onSubmit={handleSubmit} className="space-y-8 py-4">
      <div className="space-y-6">
        <div className="space-y-4">
          <Label htmlFor="title" className="text-sm font-medium">Title <span className="text-red-500">*</span></Label>
          <Input
            id="title"
            name="title"
            value={form.title}
            onChange={handleChange}
            required
            placeholder="Enter task title"
            className={`h-12 text-base ${errors.title ? 'border-red-500' : ''}`}
          />
          {errors.title && <div className="text-red-500 text-xs mt-1">{errors.title}</div>}
        </div>
        <div className="space-y-4">
          <Label htmlFor="category" className="text-sm font-medium">Category <span className="text-red-500">*</span></Label>
          <Select onValueChange={handleCategoryChange} value={form.category}>
            <SelectTrigger className={`w-full h-12 text-base ${errors.category ? 'border-red-500' : ''}`}>
              <SelectValue placeholder="Select category">
                {form.category && (
                  <div className="flex items-center gap-2">
                    {getCategoryIcon(form.category)}
                    <span>{form.category}</span>
                  </div>
                )}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="HR & Administration">
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-blue-500" />
                  <span>HR & Administration</span>
                </div>
              </SelectItem>
              <SelectItem value="IT & Technical">
                <div className="flex items-center gap-2">
                  <Settings className="h-4 w-4 text-green-500" />
                  <span>IT & Technical</span>
                </div>
              </SelectItem>
              <SelectItem value="Training & Orientation">
                <div className="flex items-center gap-2">
                  <Target className="h-4 w-4 text-purple-500" />
                  <span>Training & Orientation</span>
                </div>
              </SelectItem>
              <SelectItem value="Documentation">
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-orange-500" />
                  <span>Documentation</span>
                </div>
              </SelectItem>
            </SelectContent>
          </Select>
          {errors.category && <div className="text-red-500 text-xs mt-1">{errors.category}</div>}
        </div>
      </div>
      <div className="space-y-4">
        <Label htmlFor="description" className="text-sm font-medium">Description <span className="text-red-500">*</span></Label>
        <Textarea
          id="description"
          name="description"
          value={form.description}
          onChange={handleChange}
          placeholder="Enter task description"
          required
          rows={4}
          className={`text-base p-4 ${errors.description ? 'border-red-500' : ''}`}
        />
        {errors.description && <div className="text-red-500 text-xs mt-1">{errors.description}</div>}
      </div>

      <div className="flex justify-end space-x-2 pt-4">
        <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
          Cancel
        </Button>
        <Button type="submit">{isEdit ? 'Update Task' : 'Add Task'}</Button>
      </div>
    </form>
  );

  const renderAddTasksToOnboarding = () => (
    <div className="space-y-6 py-4">
      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          placeholder="Search tasks..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Selected count */}
      {selectedTaskIds.length > 0 && (
        <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg">
          <CheckCircle className="h-4 w-4 text-blue-600" />
          <span className="text-sm font-medium text-blue-800">
            {selectedTaskIds.length} task{selectedTaskIds.length !== 1 ? 's' : ''} selected
          </span>
        </div>
      )}

      {/* Error message */}
      {errors.tasks && (
        <div className="text-red-500 text-sm p-3 bg-red-50 rounded-lg">
          {errors.tasks}
        </div>
      )}

      {/* Tasks by category */}
      <ScrollArea className="h-[400px]">
        <div className="space-y-4">
          {Object.entries(tasksByCategory).map(([category, tasks]) => {
            const availableTasksInCategory = tasks.filter(task => !existingTaskIds.includes(task._id));
            const selectedInCategory = selectedTaskIds.filter(id =>
              availableTasksInCategory.some(task => task._id === id)
            );

            if (availableTasksInCategory.length === 0) {
              return null;
            }

            return (
              <Card key={category} className="border border-gray-200">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg" style={{ backgroundColor: getCategoryColor(category) }}>
                        {getCategoryIcon(category)}
                      </div>
                      <div>
                        <CardTitle className="text-lg">{category}</CardTitle>
                        <p className="text-sm text-gray-600">
                          {availableTasksInCategory.length} available tasks
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {availableTasksInCategory.length > 0 && (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleSelectAllInCategory(category)}
                            className="h-6 px-2 text-xs"
                          >
                            Select All
                          </Button>
                          {selectedInCategory.length > 0 && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDeselectAllInCategory(category)}
                              className="h-6 px-2 text-xs"
                            >
                              Deselect All
                            </Button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </CardHeader>

                <CardContent>
                  <div className="space-y-3">
                    {availableTasksInCategory.map((task) => (
                      <div key={task._id} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <Checkbox
                          checked={selectedTaskIds.includes(task._id)}
                          onCheckedChange={(checked) => handleTaskSelection(task._id, checked as boolean)}
                          className="mt-1"
                        />
                        <div className="flex-1 min-w-0">
                          <h5 className="font-medium text-gray-900 mb-1">
                            {task.title}
                          </h5>
                          <p className="text-sm text-gray-600 line-clamp-2">
                            {task.description}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </ScrollArea>

      <div className="flex justify-end space-x-2 pt-4 border-t">
        <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
          Cancel
        </Button>
        <Button
          type="button"
          onClick={handleAddTasksToOnboarding}
          disabled={selectedTaskIds.length === 0}
        >
          <Plus className="h-4 w-4 mr-2" />
          Add {selectedTaskIds.length} Task{selectedTaskIds.length !== 1 ? 's' : ''}
        </Button>
      </div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl w-full">
        <DialogHeader>
          <DialogTitle>
            {mode === 'add-to-onboarding'
              ? 'Add Tasks to Onboarding'
              : (isEdit ? 'Edit Onboarding Task' : 'Add Onboarding Task')
            }
          </DialogTitle>
          <DialogDescription className="sr-only">
            {mode === 'add-to-onboarding'
              ? 'Select existing onboarding tasks to add to the candidate experience.'
              : 'Create or update an onboarding task with title, category, and description.'}
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-[60vh] overflow-y-auto px-4">
          {mode === 'add-to-onboarding' ? renderAddTasksToOnboarding() : renderCreateTaskForm()}
        </div>
      </DialogContent>
    </Dialog>
  );
}; 